import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { vehiculoService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const emptyForm = {
  placa: '', categoria: '', marca: '', modelo: '',
  peso_bruto: '', peso_tara: '', peso_neto: '', carga_util: '',
  ancho: '', largo: '',
  vin: '', serie_chasis: '', anio: '', color: '',
  tarjeta_propiedad: '', nro_inscripcion_mtc: '',
};

const NUM_FIELDS = ['peso_bruto', 'peso_tara', 'peso_neto', 'carga_util', 'ancho', 'largo', 'anio'];

const Vehiculos = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vehiculoService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar vehículos.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toForm = (r) => ({
    placa: r.placa || '',
    categoria: r.categoria || '',
    marca: r.marca || '',
    modelo: r.modelo || '',
    peso_bruto: r.peso_bruto ?? '',
    peso_tara: r.peso_tara ?? '',
    peso_neto: r.peso_neto ?? '',
    carga_util: r.carga_util ?? '',
    ancho: r.ancho ?? '',
    largo: r.largo ?? '',
    vin: r.vin || '',
    serie_chasis: r.serie_chasis || '',
    anio: r.anio ?? '',
    color: r.color || '',
    tarjeta_propiedad: r.tarjeta_propiedad || '',
    nro_inscripcion_mtc: r.nro_inscripcion_mtc || '',
  });

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => { setEditing(r); setForm(toForm(r)); setShowModal(true); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === 'placa') val = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (name === 'marca') val = value.toUpperCase();
    setForm(prev => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const placa = form.placa.trim();
    if (!placa) { setAlert({ type: 'warning', message: 'La placa es obligatoria.' }); return; }
    if (!/^[A-Z0-9]+$/.test(placa)) { setAlert({ type: 'warning', message: 'La placa solo debe contener letras y números, sin guiones ni espacios.' }); return; }
    const marca = form.marca.trim();
    if (!marca) { setAlert({ type: 'warning', message: 'La marca es obligatoria.' }); return; }

    const data = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) {
        data[k] = null;
      } else if (NUM_FIELDS.includes(k)) {
        data[k] = Number(v);
      } else {
        data[k] = v;
      }
    });

    try {
      if (editing) await vehiculoService.update(editing.id_vehiculo, data);
      else await vehiculoService.create(data);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Vehículo',
      message: `¿Eliminar el vehículo "${r.placa}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await vehiculoService.delete(r.id_vehiculo);
          setToast('Vehículo eliminado');
          fetchData();
        } catch {
          setAlert({ type: 'error', message: 'Error al eliminar.' });
        }
      },
    });
  };

  const columns = [
    { key: 'placa', label: 'Placa', width: 100, style: { fontWeight: 700, letterSpacing: 2, fontFamily: 'monospace' } },
    { key: 'marca', label: 'Marca', width: 110 },
    { key: 'modelo', label: 'Modelo', width: 130 },
    { key: 'categoria', label: 'Categoría', width: 110 },
    { key: 'anio', label: 'Año', width: 60, style: { textAlign: 'center' } },
    { key: 'color', label: 'Color', width: 90 },
    { key: 'nro_inscripcion_mtc', label: 'Inscripción MTC', width: 150 },
  ];

  return (
    <Layout title="Vehículos" subtitle="Entidades">
      <div className="page-header">
        <h2>Vehículos</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable
        columns={columns} data={records} idField="id_vehiculo"
        onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay vehículos registrados"
        exportFileName="vehiculos"
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2>{editing ? 'Editar Vehículo' : 'Nuevo Vehículo'}</h2>
                {!editing && (
                  <span className="modal-header-badge">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>

                {/* ── Identificación ── */}
                <div className="form-section-title">Identificación</div>

                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr', gap: '0 16px', alignItems: 'end' }}>
                  <div className="form-row">
                    <label>Placa *</label>
                    <input
                      name="placa"
                      value={form.placa}
                      onChange={handleChange}
                      placeholder="ABC123 (sin guiones)"
                      maxLength={10}
                      autoFocus
                      style={{ fontFamily: 'monospace', letterSpacing: 2, textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-row">
                    <label>Marca *</label>
                    <input
                      name="marca"
                      value={form.marca}
                      onChange={handleChange}
                      placeholder="TOYOTA, VOLVO..."
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-row">
                    <label>Modelo</label>
                    <input name="modelo" value={form.modelo} onChange={handleChange} placeholder="Hilux, FH16..." />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: '0 16px', alignItems: 'end' }}>
                  <div className="form-row">
                    <label>Categoría</label>
                    <input name="categoria" value={form.categoria} onChange={handleChange} placeholder="Camión, Furgón, Remolcador..." />
                  </div>
                  <div className="form-row">
                    <label>Año</label>
                    <input name="anio" type="number" value={form.anio} onChange={handleChange} placeholder="2024" min={1900} max={2100} />
                  </div>
                  <div className="form-row">
                    <label>Color</label>
                    <input name="color" value={form.color} onChange={handleChange} placeholder="Blanco" />
                  </div>
                </div>

                {/* ── Pesos y dimensiones ── */}
                <div className="form-section-title" style={{ marginTop: 8 }}>Pesos y Dimensiones</div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Peso Bruto (kg)</label>
                    <input name="peso_bruto" type="number" step="0.01" value={form.peso_bruto} onChange={handleChange} placeholder="0.00" />
                  </div>
                  <div className="form-row">
                    <label>Tara (kg)</label>
                    <input name="peso_tara" type="number" step="0.01" value={form.peso_tara} onChange={handleChange} placeholder="0.00" />
                  </div>
                  <div className="form-row">
                    <label>Peso Neto (kg)</label>
                    <input name="peso_neto" type="number" step="0.01" value={form.peso_neto} onChange={handleChange} placeholder="0.00" />
                  </div>
                  <div className="form-row">
                    <label>Carga Útil (kg)</label>
                    <input name="carga_util" type="number" step="0.01" value={form.carga_util} onChange={handleChange} placeholder="0.00" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Ancho (m)</label>
                    <input name="ancho" type="number" step="0.01" value={form.ancho} onChange={handleChange} placeholder="0.00" />
                  </div>
                  <div className="form-row">
                    <label>Largo (m)</label>
                    <input name="largo" type="number" step="0.01" value={form.largo} onChange={handleChange} placeholder="0.00" />
                  </div>
                </div>

                {/* ── Datos registrales ── */}
                <div className="form-section-title" style={{ marginTop: 8 }}>Datos Registrales</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>VIN</label>
                    <input name="vin" value={form.vin} onChange={handleChange} placeholder="Número VIN" style={{ fontFamily: 'monospace' }} />
                  </div>
                  <div className="form-row">
                    <label>Serie / Chasis</label>
                    <input name="serie_chasis" value={form.serie_chasis} onChange={handleChange} placeholder="Número de serie o chasis" style={{ fontFamily: 'monospace' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Tarjeta de Propiedad</label>
                    <input name="tarjeta_propiedad" value={form.tarjeta_propiedad} onChange={handleChange} placeholder="Nro. tarjeta de propiedad" />
                  </div>
                  <div className="form-row">
                    <label>Inscripción en el MTC</label>
                    <input name="nro_inscripcion_mtc" value={form.nro_inscripcion_mtc} onChange={handleChange} placeholder="Nro. inscripción MTC" />
                  </div>
                </div>

              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      <style>{`
        .form-section-title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: var(--accent);
          border-bottom: 1px solid var(--border);
          padding-bottom: 4px;
          margin-bottom: 10px;
          margin-top: 4px;
        }
      `}</style>
    </Layout>
  );
};

export default Vehiculos;
